import { commercialTermGroups, commercialTermValue } from "../../shared/commercial-terms";
import {
  COMMERCIAL_DOCUMENT_LABELS,
  type CommercialDocumentInput,
} from "../../shared/commercial-documents";
import {
  commercialDocumentDetails,
  commercialDocumentSections,
} from "../../shared/commercial-document-layout";
import {
  COMMERCIAL_TERMS_URL,
  commercialGoverningTermsParts,
} from "../../shared/commercial-document-links";

export function CommercialDocumentPreview({
  document: doc,
}: {
  document: CommercialDocumentInput;
}) {
  return (
    <article
      aria-label="Document preview"
      className="commercial-document-preview space-y-6 rounded-md border bg-card p-5 text-foreground sm:p-8"
    >
      <header className="flex flex-wrap justify-between gap-4 border-b-2 border-primary pb-5">
        <p className="font-bold text-primary">Delta Adaptive Systems</p>
        <h3 className="text-xl font-bold">{COMMERCIAL_DOCUMENT_LABELS[doc.kind]}</h3>
      </header>
      <dl className="grid gap-3 sm:grid-cols-2">
        {commercialDocumentDetails(doc).map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs font-semibold text-muted-foreground">{label}</dt>
            <dd className="break-words text-sm">{value}</dd>
          </div>
        ))}
      </dl>
      {commercialTermGroups(doc.commercialTerms)
        .filter((group) => group.rows.length > 0)
        .map((group) => (
          <section key={group.category}>
            <h4 className="mb-2 font-semibold">{group.title}</h4>
            <table className="w-full table-fixed text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="p-2 text-left">Item</th>
                  <th className="p-2 text-left">Amount / term</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => (
                  <tr key={row.id} className="border-b">
                    <td className="whitespace-pre-wrap break-words p-2">{row.label}</td>
                    <td className="whitespace-pre-wrap break-words p-2">
                      {commercialTermValue(row)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      <DocumentSections document={doc} />
      {doc.includeAcceptance ? (
        <section>
          <h4 className="mb-3 font-semibold">Acceptance</h4>
          <div className="max-w-md">
            <p className="text-xs font-semibold">Customer</p>
            {["Authorized signature", "Name / title", "Date"].map((label) => (
              <p key={label} className="mt-7 border-t pt-1 text-xs text-muted-foreground">
                {label}
              </p>
            ))}
          </div>
        </section>
      ) : null}
      <footer className="border-t pt-3 text-xs text-muted-foreground">
        Deplyr Corp d/b/a Delta Adaptive Systems · das.dev
      </footer>
    </article>
  );
}

function DocumentSections({ document }: { document: CommercialDocumentInput }) {
  return commercialDocumentSections(document).map((section) => {
    const finePrint = section.title === "Governing terms";
    return (
      <section key={section.title} className={finePrint ? "text-xs leading-relaxed" : "text-sm"}>
        <h4 className={finePrint ? "mb-1 font-medium" : "mb-2 font-semibold"}>{section.title}</h4>
        <p className="whitespace-pre-wrap break-words">
          {finePrint ? <GoverningTermsText text={section.text} /> : section.text}
        </p>
      </section>
    );
  });
}

function GoverningTermsText({ text }: { text: string }) {
  return commercialGoverningTermsParts(text).map((part, index) =>
    part === COMMERCIAL_TERMS_URL ? (
      <a
        key={index}
        href={COMMERCIAL_TERMS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
}
